import { formatWhatsApp } from "@baze/schema";
import type { RenderContext, SectionProps } from "../context";
import { domId, messageVars } from "../context";
import { groupedHours, imageSrc, mailHref, socialLinks, srcSet, telHref, waLink } from "../helpers";
import { Icon } from "../icons";
import { Container, MultiLine, SectionShell, SiteLink, cx } from "../ui";

function Socials({ ctx, className }: { ctx: RenderContext; className?: string }) {
  const list = socialLinks(ctx.site);
  if (list.length === 0) return null;
  return (
    <ul className={cx("flex flex-wrap gap-2", className)}>
      {list.map((s) => (
        <li key={s.kind}>
          <SiteLink
            ctx={ctx}
            href={s.href}
            ariaLabel={`${s.label} ${ctx.site.name}`}
            className="grid size-11 place-items-center rounded-full border border-site-border text-site-text transition hover:border-site-primary hover:bg-site-primary hover:text-site-on-primary"
          >
            <Icon name={s.kind} className="size-[1.125rem]" />
          </SiteLink>
        </li>
      ))}
    </ul>
  );
}

function FooterLogo({ ctx, className, labelled }: { ctx: RenderContext; className?: string; labelled?: boolean }) {
  const logo = ctx.site.logo;
  const src = logo ? imageSrc(logo, 480) : null;
  if (!logo || !src) return null;
  return (
    <img
      src={src}
      srcSet={srcSet(logo)}
      sizes="200px"
      alt={logo.alt && logo.alt !== ctx.site.name ? logo.alt : labelled ? ctx.site.name : ""}
      width={logo.width}
      height={logo.height}
      loading="lazy"
      decoding="async"
      className={cx("h-12 w-auto max-w-[14rem] shrink-0 object-contain", className)}
    />
  );
}

function Copyright({ ctx }: { ctx: RenderContext }) {
  return (
    <p className="text-sm text-site-muted">
      © {ctx.year} {ctx.site.name}. Semua hak dilindungi.
    </p>
  );
}

const colHeading = "text-xs font-semibold tracking-[0.16em] text-site-muted uppercase";
const colLink = "inline-flex min-h-9 items-center gap-2 text-[0.9375rem] text-site-text transition-colors hover:text-site-primary";

/** Equal tracks for however many columns are actually visible. One column needs no grid. */
const SPREAD_ROW = [
  "grid items-start",
  "grid items-start gap-12 @2xl:grid-cols-2 @2xl:gap-10",
  "grid items-start gap-12 @2xl:grid-cols-2 @4xl:grid-cols-3 @4xl:gap-10",
  "grid items-start gap-12 @2xl:grid-cols-2 @5xl:grid-cols-4 @5xl:gap-10",
] as const;

export function Footer({ node, ctx }: SectionProps<"footer">) {
  const c = node.content;
  const site = ctx.site;
  const blurb = c.text || site.tagline;
  const blurbPath = c.text ? "content.text" : undefined;
  const showSocial = c.showSocial !== false;
  const showNav = c.showNav !== false && ctx.nav.length > 0;
  const showContact = c.showContact !== false;
  // No logo: the name is the only mark, so it stays even when the owner hid the title.
  const showTitle = c.showTitle !== false || !site.logo;

  if (node.variant === "columns") {
    // Unset lines the columns across the row. "start" packs them left; "center" centers the group.
    const align = c.align ?? "spread";
    const centered = align === "center";
    const wa = showContact ? waLink(site.contact.whatsapp, site.whatsapp?.defaultMessage, messageVars(ctx)) : null;
    const tel = showContact ? telHref(site.contact.phone) : null;
    const mail = showContact ? mailHref(site.contact.email) : null;
    const hours = c.showHours !== false ? groupedHours(site.hours) : [];
    const { address: street, city } = site.contact;
    const address = c.showAddress !== false ? (street && city && !street.includes(city) ? `${street}, ${city}` : (street ?? city ?? "")) : "";
    const hasContact = !!(wa || tel || mail || address);
    const columns = 1 + (hasContact ? 1 : 0) + (hours.length > 0 ? 1 : 0) + (showNav ? 1 : 0);
    const idContact = domId(ctx, `${node.id}-kontak`);
    const idHours = domId(ctx, `${node.id}-jam`);
    const idNav = domId(ctx, `${node.id}-nav`);
    const block = cx(centered && "flex w-full max-w-xs flex-col items-center", !centered && align !== "spread" && "max-w-xs");
    return (
      <SectionShell ctx={ctx} node={node} as="footer" padded={false} className="border-t border-site-border pt-16 pb-8 @3xl:pt-20">
        <Container>
          <div
            data-footer-align={align}
            data-footer-cols={columns}
            className={
              align === "spread"
                ? (SPREAD_ROW[Math.min(columns, SPREAD_ROW.length) - 1] ?? SPREAD_ROW[0])
                : cx(
                    "flex flex-col gap-12 @2xl:flex-row @2xl:flex-wrap @2xl:gap-x-16 @2xl:gap-y-10",
                    centered ? "items-center text-center @2xl:justify-center" : "items-start @2xl:justify-start",
                  )
            }
          >
            <div className={cx(centered ? "flex w-full max-w-sm flex-col items-center" : "max-w-sm")}>
              <FooterLogo ctx={ctx} className="mb-4" labelled={!showTitle} />
              {showTitle ? <p className="baze-heading text-2xl">{site.name}</p> : null}
              {blurb ? <MultiLine ctx={ctx} text={blurb} path={blurbPath} className="mt-3 leading-relaxed text-site-muted" /> : null}
              {showSocial ? <Socials ctx={ctx} className={cx("mt-6", centered && "justify-center")} /> : null}
            </div>
            {hasContact ? (
              <div className={block}>
                <h2 id={idContact} className={colHeading}>
                  Kontak
                </h2>
                <ul aria-labelledby={idContact} className="mt-4 space-y-1.5">
                  {wa && site.contact.whatsapp ? (
                    <li>
                      <SiteLink ctx={ctx} href={wa} whatsapp className={colLink}>
                        <Icon name="whatsapp" className="size-4 text-site-muted" />
                        {formatWhatsApp(site.contact.whatsapp)}
                      </SiteLink>
                    </li>
                  ) : null}
                  {tel ? (
                    <li>
                      <SiteLink ctx={ctx} href={tel} className={colLink}>
                        <Icon name="phone" className="size-4 text-site-muted" />
                        {site.contact.phone}
                      </SiteLink>
                    </li>
                  ) : null}
                  {mail ? (
                    <li>
                      <SiteLink ctx={ctx} href={mail} className={colLink}>
                        <Icon name="mail" className="size-4 text-site-muted" />
                        {site.contact.email}
                      </SiteLink>
                    </li>
                  ) : null}
                  {address ? (
                    <li className={cx("flex gap-2 pt-1.5 text-[0.9375rem] leading-relaxed text-site-text", centered && "justify-center text-left")}>
                      <Icon name="map-pin" className="mt-1 size-4 shrink-0 text-site-muted" />
                      <address className="not-italic">{address}</address>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
            {hours.length > 0 ? (
              <div className={block}>
                <h2 id={idHours} className={colHeading}>
                  Jam buka
                </h2>
                <ul aria-labelledby={idHours} className="mt-4 space-y-2 text-[0.9375rem]">
                  {hours.map((g) => (
                    <li key={g.label} className={cx("flex gap-4", centered ? "justify-center" : "justify-between @2xl:max-w-64")}>
                      <span className="text-site-muted">{g.label}</span>
                      <span className="text-site-text tabular-nums">{g.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {showNav ? (
              <nav aria-labelledby={idNav} className={block}>
                <h2 id={idNav} className={colHeading}>
                  Navigasi
                </h2>
                <ul className="mt-4 space-y-1">
                  {ctx.nav.map((l) => (
                    <li key={l.href}>
                      <SiteLink ctx={ctx} href={l.href} className={colLink}>
                        {l.label}
                      </SiteLink>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
          <div className={cx("mt-14 border-t border-site-border pt-6", centered && "text-center")}>
            <Copyright ctx={ctx} />
          </div>
        </Container>
      </SectionShell>
    );
  }

  // A single stack. Only "start" pulls it left; spread has nothing to distribute, so it stays centered.
  const left = c.align === "start";
  return (
    <SectionShell ctx={ctx} node={node} as="footer" padded={false} className="border-t border-site-border py-12 @3xl:py-16">
      <Container>
        <div data-footer-align={left ? "start" : "center"} className={cx("flex flex-col", left ? "items-start text-left" : "items-center text-center")}>
          <FooterLogo ctx={ctx} className="mb-4" labelled={!showTitle} />
          {showTitle ? <p className="baze-heading text-2xl">{site.name}</p> : null}
          {blurb ? <MultiLine ctx={ctx} text={blurb} path={blurbPath} className="mt-3 max-w-md leading-relaxed text-site-muted" /> : null}
          {showNav ? (
            <nav aria-label="Navigasi footer" className="mt-6">
              <ul className={cx("flex flex-wrap gap-x-2", left ? "justify-start" : "justify-center")}>
                {ctx.nav.map((l) => (
                  <li key={l.href}>
                    <SiteLink ctx={ctx} href={l.href} className="inline-flex min-h-11 items-center px-2 text-sm font-medium text-site-muted hover:text-site-text">
                      {l.label}
                    </SiteLink>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
          {showSocial ? <Socials ctx={ctx} className={cx("mt-6", !left && "justify-center")} /> : null}
          {c.showAddress !== false && site.contact.address ? (
            <address className="mt-6 text-sm text-site-muted not-italic">{site.contact.address}</address>
          ) : null}
          {c.showHours !== false && site.hours?.length ? (
            <p className={cx("mt-2 flex flex-wrap gap-x-3 text-sm text-site-muted", left ? "justify-start" : "justify-center")}>
              {groupedHours(site.hours).map((g) => (
                <span key={g.label}>
                  {g.label}: <span className="tabular-nums">{g.value}</span>
                </span>
              ))}
            </p>
          ) : null}
          <div className="mt-8">
            <Copyright ctx={ctx} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

